import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Plus, Copy, ShieldCheck, Ban, FolderOpen, Download, X, Check } from 'lucide-react';

const STATUS_CLASS = { active: 'scx-status-active', expired: 'scx-status-expired', revoked: 'scx-status-revoked' };

const SecureCX = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [links, setLinks] = useState([]);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [openDocsFor, setOpenDocsFor] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const lobId = lob?.id;
  const load = useCallback(() => {
    if (!lobId) return;
    api.get(`/securecx?lob_id=${lobId}`).then((r) => setLinks(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [lobId]);
  useEffect(() => { load(); }, [load]);

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link.public_url);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const revoke = async (link) => {
    if (!window.confirm(`Revoke "${link.label}"? The link will stop working immediately.`)) return;
    try { await api.post(`/securecx/${link.id}/revoke`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-toolbar" data-testid="securecx-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">SecureCX</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid="securecx-generate-btn">
          <Plus size={11} style={{ marginRight: 4 }} />Generate link
        </button>
      </div>

      {err && <div className="module-banner err">{err}</div>}

      <div className="content-area">
        {links.length === 0 ? (
          <div className="empty-state" data-testid="securecx-empty">
            <ShieldCheck size={36} style={{ opacity: 0.2 }} />
            <div className="ttl">No secure links yet</div>
            <div className="sub">Generate a no-login link so clients can securely upload documents.</div>
          </div>
        ) : (
          <div className="card">
            <table className="tbl">
              <thead><tr><th>Label</th><th>Link</th><th>Status</th><th>Documents</th><th>Requested</th><th>Expires</th><th></th></tr></thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.id} data-testid={`securecx-row-${l.id}`}>
                    <td>{l.label}</td>
                    <td>
                      <span className="scx-token-pill">
                        …/secure/{l.token.slice(0, 8)}…
                        <button className="scx-copy-btn" onClick={() => copyLink(l)} data-testid={`securecx-copy-${l.id}`}>
                          {copiedId === l.id ? <Check size={11} /> : <Copy size={11} />}
                        </button>
                      </span>
                    </td>
                    <td><span className={STATUS_CLASS[l.status]} data-testid={`securecx-status-${l.id}`}>{l.status}</span></td>
                    <td>
                      <button className="btn-link" onClick={() => setOpenDocsFor(l)} data-testid={`securecx-docs-btn-${l.id}`}>
                        <FolderOpen size={11} style={{ marginRight: 3, verticalAlign: -1 }} />{l.document_count}
                      </button>
                    </td>
                    <td>
                      {l.requested_total > 0 ? (
                        <span
                          className={l.requested_fulfilled >= l.requested_total ? 'scx-status-active' : ''}
                          data-testid={`securecx-progress-${l.id}`}
                        >
                          {l.requested_fulfilled}/{l.requested_total} received
                        </span>
                      ) : <span style={{ color: 'var(--text-faded)' }}>—</span>}
                    </td>
                    <td>{new Date(l.expires_at).toLocaleDateString()}</td>
                    <td>
                      {l.status === 'active' && (
                        <button className="btn-link" onClick={() => revoke(l)} style={{ color: 'var(--error)' }} data-testid={`securecx-revoke-${l.id}`}>
                          <Ban size={11} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <CreateLinkModal lob={lob} onClose={(saved) => { setShowCreate(false); if (saved) load(); }} />}
      {openDocsFor && <DocumentsDrawer link={openDocsFor} onClose={() => setOpenDocsFor(null)} />}
    </>
  );
};

const CreateLinkModal = ({ lob, onClose }) => {
  const [label, setLabel] = useState('');
  const [contactId, setContactId] = useState('');
  const [dealId, setDealId] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [reqInput, setReqInput] = useState('');
  const [requested, setRequested] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get(`/contacts?lob_id=${lob.id}`).then((r) => setContacts(r.data)).catch(() => {});
    api.get(`/deals?lob_id=${lob.id}`).then((r) => setDeals(r.data)).catch(() => {});
  }, [lob.id]);

  const addRequested = () => {
    const v = reqInput.trim();
    if (v && !requested.includes(v)) setRequested((r) => [...r, v]);
    setReqInput('');
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const { data } = await api.post('/securecx', {
        lob_id: lob.id, label, requested_documents: requested,
        contact_id: contactId || null, deal_id: dealId || null,
        expires_in_days: Number(expiresInDays) || 14,
      });
      setCreated(data);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  if (created) {
    return (
      <div className="modal-overlay-v2" onClick={() => onClose(true)}>
        <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="securecx-created-modal">
          <div className="modal-title">Link ready</div>
          <div className="scx-token-pill" style={{ display: 'flex', marginTop: 8, wordBreak: 'break-all', fontSize: 11 }} data-testid="securecx-created-url">
            {created.public_url}
          </div>
          <button
            type="button" className="btn-primary" style={{ marginTop: 14, width: '100%' }}
            onClick={() => { navigator.clipboard?.writeText(created.public_url); }}
            data-testid="securecx-created-copy-btn"
          >
            <Copy size={11} style={{ marginRight: 5 }} />Copy link
          </button>
          <div className="modal-actions"><button type="button" className="btn-secondary" onClick={() => onClose(true)} data-testid="securecx-created-done-btn">Done</button></div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="securecx-create-modal">
        <div className="modal-title">Generate secure link</div>
        <label className="f-label">Label</label>
        <input className="f-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Documents for John Smith" required data-testid="securecx-label-input" />

        <div className="modal-row" style={{ marginTop: 8 }}>
          <div>
            <label className="f-label">Contact (optional)</label>
            <select className="f-select" value={contactId} onChange={(e) => setContactId(e.target.value)} data-testid="securecx-contact-select">
              <option value="">— None —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.first} {c.last}</option>)}
            </select>
          </div>
          <div>
            <label className="f-label">Deal (optional)</label>
            <select className="f-select" value={dealId} onChange={(e) => setDealId(e.target.value)} data-testid="securecx-deal-select">
              <option value="">— None —</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>

        <label className="f-label" style={{ marginTop: 8 }}>Requested documents</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="f-input" value={reqInput} onChange={(e) => setReqInput(e.target.value)}
            placeholder="e.g. Bank Statement" data-testid="securecx-req-input"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRequested(); } }}
          />
          <button type="button" className="btn-secondary" onClick={addRequested} data-testid="securecx-req-add-btn">Add</button>
        </div>
        {requested.length > 0 && (
          <div style={{ marginTop: 8 }} data-testid="securecx-req-chips">
            {requested.map((r) => (
              <span key={r} className="scx-req-chip">{r}<button type="button" onClick={() => setRequested((list) => list.filter((x) => x !== r))}><X size={10} /></button></span>
            ))}
          </div>
        )}

        <label className="f-label" style={{ marginTop: 8 }}>Expires in (days)</label>
        <input type="number" min={1} max={90} className="f-input" value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)} data-testid="securecx-expiry-input" />

        {err && <div className="f-error" data-testid="securecx-create-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="securecx-create-submit">{busy ? 'Generating…' : 'Generate link'}</button>
        </div>
      </form>
    </div>
  );
};

const DocumentsDrawer = ({ link, onClose }) => {
  const [docs, setDocs] = useState([]);
  const [err, setErr] = useState('');
  const [dlBusy, setDlBusy] = useState(null);

  useEffect(() => {
    api.get(`/securecx/${link.id}/documents`).then((r) => setDocs(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [link.id]);

  const download = (doc) => {
    setDlBusy(doc.id);
    api.get(`/securecx/documents/${doc.id}/download`, { responseType: 'blob' })
      .then(({ data }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([data], { type: doc.content_type || 'application/octet-stream' }));
        a.download = doc.filename;
        a.click();
      })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setDlBusy(null));
  };

  return (
    <div className="scx-drawer-overlay" onClick={onClose} data-testid="securecx-docs-drawer">
      <div className="scx-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="scx-drawer-hd">
          <div>
            <div className="modal-title" style={{ marginBottom: 2 }}>{link.label}</div>
            <div className="sub" style={{ fontSize: 11, color: 'var(--text-faded)' }}>{docs.length} document{docs.length === 1 ? '' : 's'} uploaded</div>
          </div>
          <button className="btn-link" onClick={onClose} data-testid="securecx-drawer-close"><X size={16} /></button>
        </div>
        {err && <div className="module-banner err">{err}</div>}
        {docs.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px 0' }} data-testid="securecx-docs-empty">
            <div className="sub">No documents uploaded to this link yet.</div>
          </div>
        ) : (
          docs.map((d) => (
            <div key={d.id} className="scx-doc-row" data-testid={`securecx-doc-row-${d.id}`}>
              <div className="scx-doc-meta">
                <div className="scx-doc-name">{d.filename}</div>
                <div className="scx-doc-sub">{d.requested_doc ? `${d.requested_doc} · ` : ''}{d.uploaded_by_email} · {new Date(d.uploaded_at).toLocaleString()}</div>
              </div>
              <button className="btn-link" disabled={dlBusy === d.id} onClick={() => download(d)} data-testid={`securecx-doc-download-${d.id}`}>
                <Download size={13} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SecureCX;
